import { showUsaFeature } from 'utils/app';
import { Activity, BloodPressureUnit, HeartRateUnit, SymptomSeverity, UnitType, WeightUnit } from './generic';

export const RuleTypes = {
  group: 'group',
  individual: 'individual',
};

export type RuleType = (typeof RuleTypes)[keyof typeof RuleTypes];

export const RuleStatuses = {
  draft: 'draft',
  inReview: 'in-review',
  approved: 'approved',
  enabled: 'enabled',
};

export type RuleStatus = (typeof RuleStatuses)[keyof typeof RuleStatuses];

export const RuleTransitions = {
  submit: 'submit',
  reset: 'reset',
  approve: 'approve',
  enable: 'enable',
  disable: 'disable',
};
export type RuleTransition = (typeof RuleTransitions)[keyof typeof RuleTransitions];

export interface RuleItem {
  id: string;
  status: RuleStatus;
  type?: RuleType;
  notification: {
    title: string;
    description: string;
    priority: PriorityKeys;
  };
  targets: { patientIds: string[]; groupIds: string[] };
  recipients: { groupIds: string[] };
  excludes: { patientIds?: string[] };
  excluded?: boolean;
  patientId?: string;
  groupId: string;
  ruleCreationTimestamp: Date;
  extras: { template: string };
  approval?: { timestamp: Date; userId: string; userFirstName: string; userLastName: string };
}

export const Priority = {
  Critical: 1000,
  High: 750,
  Medium: 500,
  Low: 250,
};

export type PriorityKeys = (typeof Priority)[keyof typeof Priority];

export const RuleTriggers = {
  heartRhythmTransition: 'heart-rhythm-transition',
  heartRhythmMeasurement: 'heart-rhythm-measurement',
  physiologicalParameterChange: 'physiological-parameter-change',
  physiologicalParameterThreshold: 'physiological-parameter-threshold',
  physiologicalParameterMissing: 'physiological-parameter-missing',
  heartRhythmNote: 'user-adds-note',
  reportGenerated: 'report-generated',
  timeSincePrescription: 'time-since-prescription',
};

export type RuleTrigger = (typeof RuleTriggers)[keyof typeof RuleTriggers];

export const RuleReportGenerations = showUsaFeature
  ? {
      summary: 'summary-report-generated',
      billing: 'billing-report-generated',
    }
  : {
      summary: 'summary-report-generated',
    };

export const RuleFrequencies = {
  occurrence: 'occurrence',
  prescription: 'prescription',
};

export type RuleFrequency = (typeof RuleFrequencies)[keyof typeof RuleFrequencies];

export const RuleTimeFrames = {
  minutes: 'minutes',
  hours: 'hours',
  days: 'days',
};

export enum TimeFramesInSeconds {
  minutes = 60,
  hours = 60 * 60,
  days = 24 * 60 * 60,
}

export type RuleTimeFrame = (typeof RuleTimeFrames)[keyof typeof RuleTimeFrames];

export const RuleConditions = {
  above: 'above',
  below: 'below',
};

export type RuleCondition = (typeof RuleConditions)[keyof typeof RuleConditions];

export const RuleParameters = {
  weight: 'weight',
  systolicBloodPressure: 'systolic-blood-pressure',
  diastolicBloodPressure: 'diastolic-blood-pressure',
  heartRate: 'heart-rate',
};

export type RuleParameter = (typeof RuleParameters)[keyof typeof RuleParameters];

export const RuleParametersDefaults = [
  { unit: WeightUnit.Pounds, defaultValue: 165, parameter: RuleParameters.weight, unitType: UnitType.Imperial },
  { unit: WeightUnit.Kilograms, defaultValue: 75, parameter: RuleParameters.weight, unitType: UnitType.Metric },
  { unit: BloodPressureUnit.MillimetersOfMercury, defaultValue: 130, parameter: RuleParameters.systolicBloodPressure },
  { unit: BloodPressureUnit.MillimetersOfMercury, defaultValue: 90, parameter: RuleParameters.diastolicBloodPressure },
  { unit: HeartRateUnit.BeatsPerMinute, defaultValue: 90, parameter: RuleParameters.heartRate },
];

export const RuleDirections = {
  increased: 'increased',
  decreased: 'decreased',
};

export type RuleDirection = (typeof RuleDirections)[keyof typeof RuleDirections];

export interface RuleDataForm {
  id?: string;
  formDisabled: boolean;
  formActionsDisabled: boolean;
  trigger: RuleTrigger;
  name?: string;
  description?: string;
  priority: string;
  frequency: RuleFrequency;
  patientId?: string;
  groupId?: string;
  toStatus?: RuleStatus;
  data: {
    heartRhythmTransition?: {
      from?: string[];
      to?: string[];
      numberOfMeasurements: {
        amount?: number;
        isConsecutive?: boolean;
      };
    };
    heartRhythmMeasurement: {
      oneConditionEnabled?: boolean;
      numberOfMeasurementsCondition: {
        enabled: boolean;
        numberOfMeasurements: {
          amount?: number;
          timeWindow?: number;
          isConsecutive: boolean;
        };
      };
      numberOfDaysCondition?: {
        enabled: boolean;
        numberOfDays: {
          amount?: number;
          timeWindow?: number;
          isConsecutive: boolean;
        };
      };
      indicatorCondition?: { enabled: boolean; indicator: string[] };
      reviewedLabelCondition?: { enabled: boolean; reviewedLabel: string[] };
      heartRateCondition?: { enabled: boolean; heartRate: { condition: RuleCondition; value?: number } };
      symptomsCondition?: { enabled: boolean; symptoms: string[] };
      symptomSeverityCondition?: { enabled: boolean; symptomSeverity?: SymptomSeverity };
      activityCondition?: { enabled: boolean; activity: Activity[] };
      includeBadQuality?: boolean;
    };
    physiologicalParameterChange: {
      parameter?: RuleParameter;
      direction: RuleDirection;
      value?: number;
      timeWindow?: number;
    };
    physiologicalParameterThreshold: {
      parameter?: RuleParameter;
      condition: RuleCondition;
      threshold?: number;
    };
    physiologicalParameterMissing: {
      parameter?: RuleParameter;
      timeWindow?: number;
    };
    summaryReportGenerated: {
      indicator?: string[];
      isEmpty?: boolean;
    };
    timeSincePrescription: {
      timeWindow?: number;
    };
  };
}
